<template>  
  <div class="modal-card modal-card--xxl">
    <header class="modal-card-head">
      <p class="modal-card-title">
        Select Multiple Products
      </p>
      <a class="close-modal" aria-label="close" @click="closeModal()">
        <i class="material-icons">close</i>
      </a>
    </header>
    <section class="modal-card-body">
      <div class="multi-product-body-row">
        <div>
          <div class="select-container">
            <div class="select-sticky">

              <div class="search-container">
                <i class="material-icons search-icon">search</i>
                <input type="text" class="input" placeholder="Search products..." v-model="filter">
                <i class="material-icons close-icon" v-if="filter && filter.length" @click="filter = ''">close</i>
              </div>

              <div class="toggle-row">
                <span class="pseudo-link" @click="selectAll()">Select <span v-if="!filter.length">all</span><span v-else>visible</span></span>/
                <span class="pseudo-link" @click="deselectAll()">Deselect <span v-if="!filter.length">all</span><span v-else>visible</span></span>/
                <span class="pseudo-link" @click="toggleAll()">Toggle <span v-if="!filter.length">all</span><span v-else>visible</span></span>
              </div>
            </div>
            <div class="select-scrollable">
              <div class="select-inner" :class="{'select-inner--empty': !productsArrayFiltered.length}">
                <div class="select-all-empty" v-if="!productsArrayFiltered.length">
                  Empty
                </div>

                <ProductTreeLevel 
                  :items="productsArrayFiltered" 
                  :internalSelectionState="internalSelectionState" 
                  @select="selectClick($event)"
                  :filter="filter"
                />
                
              </div>
            </div>
          </div>
        </div>
        <div class="right-col">
          <h5>
            Currently Selected
          </h5>
          <div class="remove-all" v-if="selectedProductsForRightCol && selectedProductsForRightCol.length">
            <span class="pseudo-link" @click="removeAll()">
              Remove all
            </span>
          </div>
          <div class="selected-list" v-if="selectedProductsForRightCol && selectedProductsForRightCol.length">
            <div
              v-for="(prod, id) in selectedProductsForRightCol"
              :key="id"
            >
              <div class="selected-item__title">
                {{ prod.displayHierarchy || prod.display || prod.id }}
              </div>
              <div class="selected-item__detail" v-if="prod.displayHierarchy">
                {{ returnSelectedItemDetail(prod) }}
              </div>
              
              <div class="remove-selected-item" @click="selectClick(prod, 'Unselected')">
                <i class="material-icons">close</i>
              </div>
            </div>
          </div>
          <div class="selected-list--empty" v-else>
            No products selected
          </div>
        </div>
      </div>

      <div class="buttons-row">
        <button class="button button-secondary button-outline button-medium"
          @click="closeModal()"
        >
          Cancel
        </button>
        <button class="button button-primary button-medium" @click="applyChanges()">
          Apply
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import _ from 'lodash';
import ProductTreeLevel from '@/components/ProductTreeLevel.vue';
import { Product, SelectionStateMap, SelectionStateOption, ProductsMappedByID, ProductWithChildren } from '../../store/types';
import GAService from '@/services/ga.service';

@Component({
  components: {
    ProductTreeLevel
  },
})
export default class MultiProductModal extends Vue {

  public filter: string = '';

/** **********************************************************
 * 
 *          Product List (for reference, and display)
 *  
 ********************************************************** */

  get productsById(): ProductsMappedByID {
    return this.$store.state.options.productsById || {};
  }

  /** 
   * A recursive function that goes through each level of our product tree,
   * and derives children products from each product's array of child IDs.
   * 
   * We traverse upwards so that each derived child product will itself 
   * already have its own, fully derived child products as part of it
   */
  public traverseUpByLevel(arr: ProductWithChildren[], level: Product[]): ProductWithChildren[] {
    let parentLevelIds: string[] = [];

    let transformedLevel = level.forEach( (item) => {
      // Add unique parent ids to array
      if (item.parent && parentLevelIds.indexOf(item.parent) < 0) {
        parentLevelIds.push(item.parent);
      }

      let childObjs: ProductWithChildren[] = [];
      if (item.directChildren.length) {
        item.directChildren.forEach( (id) => {
          let fullProd = arr.find((contender) => contender.id === id);
          if (fullProd) {
            childObjs.push(fullProd);
          }
        });
      }

      const existing = arr.find(prod => prod.id === item.id);
      if (existing) {
        childObjs.forEach(child => {
          if (!existing.directChildrenObjs.find(existingChild => child.id === existingChild.id)) {
            existing.directChildrenObjs = existing.directChildrenObjs.concat(child)
          }
        });
      } else {
        arr.push(new ProductWithChildren(item, childObjs))
      }
    });

    if (parentLevelIds.length) {
      const parentLevelItems = parentLevelIds.map( (id) => this.productsById[id] );
      return this.traverseUpByLevel(arr, parentLevelItems);
    } else {
      return arr;
    }
  }

  /**
   * Returns all products, adding complete child nodes to each 
   * product as part of the process
   */
  get productsArray(): ProductWithChildren[] {
    const raw = _.values(this.productsById) || [];

    // get all leaf nodes
    let leafs = raw.filter((prod) => {
      return !prod.directChildren.length;
    });

    // Go through each level, adding full child objects
    return this.traverseUpByLevel([], leafs);
  }

  /**
   * Filter our product list so that we only have the top level of 
   * products in our returned array. Each of these products will 
   * have child products nested within in, if available.
   */
  get productsArrayTopLevel(): ProductWithChildren[] {
    return this.productsArray.filter((prod) => prod.level === 1) || [];
  }

  /** 
   * A recursive function that goes down through each level to see if 
   * a branch matches our filter criteria.
   * 
   * Note: if a descendent matches our filter criteria, we will need to
   * retain its whole branch: ie its parent (and its parent, and so on)
   */
  filterLevelsDescending(level: ProductWithChildren[], filter: string): ProductWithChildren[] {
    const filteredLevel: ProductWithChildren[] = [];
    level.forEach((prod) => {
      if (prod.display && prod.display.toLowerCase().indexOf(filter) > -1) {
        // If the top level matches, all children/descendents should be shown
        filteredLevel.push(prod);
      } else {
        let directChildrenObjs = this.filterLevelsDescending(prod.directChildrenObjs, filter);
        if (directChildrenObjs.length) {
          filteredLevel.push({ ...prod, directChildrenObjs });
        }
      }
    });

    return filteredLevel;
  }

  /**
   * The products array that gets used in the UI. If no search/filter is 
   * present, this simply returns productsArrayTopLevel.
   * If there is a filter, this applies the filterLevelsDescending function 
   * to the productsArrayTopLevel, returning the result of that
   */
  get productsArrayFiltered(): ProductWithChildren[] {
    if (this.filter && this.filter.length) {
      let filter = this.filter.toLowerCase();
      return this.filterLevelsDescending(this.productsArrayTopLevel.slice(), filter);
    }

    return this.productsArrayTopLevel;
  }

/** **********************************************************
 * 
 *          Selected products
 *  
 ********************************************************** */

  public internalSelectionState: SelectionStateMap = {};

  /**
   * Currently selected products in the store. This does not
   * track internal changes in this component.
   * It IS used to populate our initial selections though, when
   * this component is mounted.
   * It is updated when Save/Apply is clicked.
   */
  get selectedProducts(): string[] {
    return this.$store.state.selections.selectedProducts || [];
  }

  /**
   * A (potentially) recursive function that looks down through a branch 
   * and tracks the highest level at which our internalSelectionState
   * indicates the node is in a Selected state.
   */
  public findHighestSelectedNodeInBranch(level: ProductWithChildren[], selectedArr: ProductWithChildren[]) {
    level.forEach((prod) => {
      if (this.internalSelectionState[prod.id] &&
        this.internalSelectionState[prod.id] === 'Selected'
      ) {
        selectedArr.push(prod)
      } else if (this.internalSelectionState[prod.id] &&
        this.internalSelectionState[prod.id] === 'Fuzzy' &&
        prod.directChildrenObjs.length
      ) {
        this.findHighestSelectedNodeInBranch(prod.directChildrenObjs, selectedArr);
      }
    });

    return selectedArr;
  }
  /** 
   * Shows the selected products as they will be shown in the right hand 
   * column, and also how they will persist in the store, and be sent 
   * with queries, etc.
   */
  get selectedProductsForRightCol(): ProductWithChildren[] {
    return this.findHighestSelectedNodeInBranch(this.productsArrayTopLevel, []); 
  }

  public returnSelectedItemDetail(prod: ProductWithChildren) {
    let rawStr = prod.display || prod.id;
    if (rawStr.indexOf(' - ') > -1) {
      let arr = rawStr.split(' - ');
      arr.pop();
      let newStr = arr.join(' - ');
      return newStr;
    }

    return rawStr;
  }


/** **********************************************************
 * 
 *          Selection triggers from component UI
 *  
 ********************************************************** */

  public findLeafNodes(arr: ProductWithChildren[], products: ProductWithChildren[]) {
    products.forEach(prod => {
      if (prod.directChildrenObjs.length) {
        return this.findLeafNodes(arr, prod.directChildrenObjs)
      } else {
        arr.push(prod);
      }
    });

    return arr;
  }   

  public selectAll() {
    if (!this.filter.length) {
      this.productsArrayFiltered.forEach( (item) => {
        this.selectClick(item, 'Selected')
      });
    } else {
      let visibleLeafs = this.findLeafNodes([], this.productsArrayFiltered);
      visibleLeafs.forEach( (item) => {
        this.selectClick(item, 'Selected')
      });
    }
  }

  public deselectAll() {
    if (!this.filter.length) {
      this.internalSelectionState = {};
    } else {
      let visibleLeafs = this.findLeafNodes([], this.productsArrayFiltered);
      visibleLeafs.forEach( (item) => {
        this.selectClick(item, 'Unselected')
      });
    }
  }

  public removeAll() {
    this.internalSelectionState = {};
  }

  public toggleAll() {
    if (!this.filter.length) {
      this.productsArray.forEach((prod) => {
        if (!prod.directChildrenObjs.length) {
          this.selectClick(prod);
        }
      });
    } else {
      let visibleLeafs = this.findLeafNodes([], this.productsArrayFiltered);
      visibleLeafs.forEach( (item) => {
        this.selectClick(item);
      });
    }
  }

/** **********************************************************
 * 
 *                     Selection Logic
 *  
 ********************************************************** */

  /** 
   * Called when a parent node has been explicitly set as 
   * Selected or Unselected.
   * Traverses all descendents and gives them that same status
   */
  public setChildStatus(newObj: SelectionStateMap, prod: ProductWithChildren, status: SelectionStateOption) {
    prod.directChildrenObjs.forEach((obj: any) => {
      newObj[obj.id] = status;
      if (obj.directChildrenObjs.length) {
        newObj = this.setChildStatus(newObj, obj, status);
      }
    });

    return newObj;
  }

  checkAscendents(newObj: SelectionStateMap, prod: ProductWithChildren, fn: Function): any {
    const parentProd = this.productsArray.find( (contender: any) => contender.id === prod.parent );
    if (!parentProd) {
      return newObj;
    }
    
    newObj = fn(newObj, parentProd);

    if (parentProd && parentProd.parent) {
      return this.checkAscendents(newObj, parentProd, fn);
    }

    return newObj;
  }

  checkParentStatus(newObj: SelectionStateMap, parent: ProductWithChildren) {
    let seen = { selected: 0, unselected: 0 };
    const length = parent.directChildrenObjs.length;
    if (length) {
      for (let i = 0; i < length; i++) {
        const obj = parent.directChildrenObjs[i];
        if (newObj[obj.id] && newObj[obj.id] === 'Selected') {
          seen.selected++;
        } else if (newObj[obj.id] && newObj[obj.id] === 'Fuzzy') {
          seen.selected++;
          seen.unselected++;
        } else {
          seen.unselected++;
        }

        if (seen.selected && seen.unselected) {
          break;
        }
      };
    }

    if (seen.selected && seen.unselected) {
      newObj[parent.id] = 'Fuzzy';
    } else if (seen.selected) {
      newObj[parent.id] = 'Selected';
    } else if (seen.unselected) {
      newObj[parent.id] = 'Unselected';
    }

    return newObj;
  }

  /** 
   * Respond to a click on a left col row. Emitted from ProductTreeLevel component, or
   * triggered by select all/deselect all/toggle all.
   * 
   * It determines the selection status of the row by looking at the forceStatus param, or
   * based on the current state. Selection statuses are stored local to this component in 
   * the internalSelectionState map.
   * 
   * Based on this change, we also adjust the selection status of the node's relations, for
   * detail see this.setChildStatus, and this.checkAscendents
   */
  public selectClick(prod: ProductWithChildren, forceStatus?: SelectionStateOption) {
    let newObj = {...this.internalSelectionState};

    if (this.filter.length) {
      this.selectClickWithFilter(prod, forceStatus);
      return;
    }    

    let status: SelectionStateOption | undefined = undefined;
    let existingStatus = newObj[prod.id] || 'Unselected';
    let intendedStatus: SelectionStateOption = existingStatus !== 'Selected' ? 'Selected' : 'Unselected';
    // Set status of the clicked item
    status = forceStatus || intendedStatus;
    newObj[prod.id] = status;

    const isHierarchical = prod.display.indexOf('Securities and listed derivatives') > -1;
    if (!isHierarchical) { // if this is not part of a hierarchy, we can finish here!
      this.internalSelectionState = {...newObj};
      return;
    }
    
    // Set all the visible children to the force or intended status
    newObj = this.setChildStatus(newObj, prod, forceStatus || intendedStatus);

    // We have set the status of our product and its children, so we need to see 
    // how these changes effect its ascendents, if it has any
    if (prod.level > 1) {
      newObj = this.checkAscendents(newObj, prod, this.checkParentStatus);
    }
    
    // Finally, set our new selection values to the state
    this.internalSelectionState = {...newObj};
  }

  public selectClickWithFilter(prod: ProductWithChildren, forceStatus?: SelectionStateOption) {
    let newObj = {...this.internalSelectionState};
    let fullProduct = this.productsArray.find( (contender: any) => contender.id === prod.id ) || prod;
    const isHierarchical = prod.display.indexOf('Securities and listed derivatives') > -1;

    let status: SelectionStateOption | undefined = undefined;
    let existingStatus = newObj[prod.id] || 'Unselected';
    let intendedStatus: SelectionStateOption = existingStatus !== 'Selected' ? 'Selected' : 'Unselected';

    if (!isHierarchical || !fullProduct.directChildrenObjs.length) {
      status = forceStatus || intendedStatus;
      newObj[prod.id] = status;
    }

    if (!isHierarchical) {
      this.internalSelectionState = {...newObj};
      return;
    }
    
    if (!status) {
      if (existingStatus === 'Fuzzy') {
        status = 'Unselected';
        const isOneVisibleChildUnselected = prod.directChildrenObjs.find((child) => {
          return !newObj[child.id] || (newObj[child.id] && newObj[child.id] === 'Unselected');
        });
        if (isOneVisibleChildUnselected) {
          status = 'Selected';
        }
        

        newObj = this.setChildStatus(newObj, prod, status);
        newObj = this.checkParentStatus(newObj, fullProduct);
      } else {
        status = existingStatus === 'Selected' ? 'Unselected' : 'Selected';
        if (fullProduct.directChildrenObjs.length === prod.directChildrenObjs.length) {
          newObj[prod.id] = status;
          newObj = this.setChildStatus(newObj, prod, status);
          newObj = this.checkAscendents(newObj, fullProduct, this.checkParentStatus);
        } else {
          const leafs = this.findLeafNodes([], [prod]);
          leafs.forEach( (item) => {
            this.selectClick(item, status);
          });
          return;
        }
      }
    } else {
      newObj = this.setChildStatus(newObj, prod, forceStatus || intendedStatus);
      if (prod.level > 1) {
        newObj = this.checkAscendents(newObj, fullProduct, this.checkParentStatus);
      }
    }

    this.internalSelectionState = {...newObj};
  } 
  

/** **********************************************************
 * 
 *  
 *                  Init, Apply, Cancel
 *  
 * 
 ********************************************************** */

  public applyChanges() {
    this.$store.dispatch('clearSelections');

    if (!this.selectedProductsForRightCol.length) {
      GAService.sendEvent(this.$gtag, 'Product', 'Removed from Multi Select', '');
      this.$store.dispatch('clearSelections');
    } else {
      const ids = this.selectedProductsForRightCol.map((prod) => prod.id);
      GAService.sendEvent(this.$gtag, 'Product', 'Applied from Multi Select', ids.join());
      this.$store.commit('setSelectedProducts', ids);
    }
    this.closeModal();
  }

  public closeModal() {
    this.$store.commit('setModal', '');
  }

  public mounted() {
    this.selectedProducts.map((prodId: string) => {
      const prod = this.productsArray.find((prod) => prod.id == prodId);
      if (prod) {
        this.selectClick(prod, 'Selected');
      } else {
        console.error('we cannot find a full product for a selected product id we have in the store.');
      }
    });
  }

}

</script>

<style scoped lang="scss">
@import '@/scss/partials/_settings.colors.scss';
.modal .modal-card {
  height: calc(100vh - 40px);

  .modal-card-body {
    display: flex;
    //border: 2px solid orange;
    flex-direction: column;
    flex-grow: 1;
    padding: 20px;

    > div {
      display: flex;
    }

    .multi-product-body-row {
      display: flex;
      //border: 2px solid limegreen;
      flex-grow: 1;
      min-height: 0;

      > div {
        flex: 1;
        display: flex;
        min-height: 0;

        .select-container {
          background-color: #fff;
          border: 1px solid $border-color;
          flex-grow: 1;
          display: flex;
          position: relative;
          padding-top: 65px;


          .select-sticky {
            display: flex;
            flex-direction: column;
            padding: 0;
            font-size: 14px;
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            background-color: #fff;
            z-index: 1;

            .button {
              font-size: 13px;
              padding-top: 5px;
              padding-bottom: 5px;

              span {
                font-size: 13px;
              }
            }

            .search-container {
              width: 100%;
              height: 37px;
              border-bottom: 1px solid $border-color;
              position: relative;
              font-size: 14px;

              .input {
                border-radius: 0;
                box-shadow: none;
                padding-left: 40px;
                padding-right: 40px;
                height: 36px;
                border: 0;
                font-size: 16px;
              }

              .search-icon {
                position: absolute;
                top: 0;
                left: 0;
                bottom: 0;
                width: 40px;
                line-height: 36px;
                z-index: 1;
                font-size: 18px;
                color: $grey-120;
              }

              .close-icon {
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                width: 40px;
                line-height: 36px;
                font-size: 18px;
                color: $grey-120;

                &:hover {
                  cursor: pointer;
                  color: $refinitiv-blue;
                }
              }
            }

            .toggle-row {
              border-bottom: 1px solid $border-color;
              text-align: left;
              padding: 3px 0 3px 14px;

              .pseudo-link {
                padding: 0 5px;

                &:first-child {
                  padding-left: 0;
                }
              }
            }
          }

          .select-scrollable {
            overflow-y: scroll;
            flex-grow: 1;

            .select-inner {
              padding: 0;
              min-height: 100%;
              display: flex;
              flex-direction: column;

              &--empty {
                justify-content: center;
              }
            }
          }
        }
      }

      .right-col {
        padding-left: 30px;
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        display: flex;

        .remove-all {
          text-align: left;
          font-size: 14px;
        }

        .selected-list {
          margin-top: 10px;
          text-align: left;
          flex-grow: 1;
          display: flex;
          flex-direction: column;
          overflow-y: scroll;
          border: 1px solid $border-color;

          > div {
            border-bottom: 1px solid $border-color;
            position: relative;
            padding: 10px 50px 10px 10px;

            .selected-item__title {
              font-size: 16px;
              font-weight: 500;
            }

            .selected-item__detail {
              font-size: 12px;
            }

            .remove-selected-item {
              position: absolute;
              top: 0;
              right: 0;
              bottom: 0;
              width: 50px;
              text-align: center;
              display: flex;
              align-items: center;
              justify-content: center;
              color: $grey-180;

              i {
                font-size: 18px;
              }

              &:hover {
                cursor: pointer;
                color: $refinitiv-blue;
              }
            }
          }
        }

        .selected-list--empty {
          flex-grow: 1;
          display: flex;
          padding-top: 10px;
        }
      }
    }

    .buttons-row {
      margin-top: 10px;
    }
  }
}

</style>